import React, { useState, useEffect } from 'react';
import { Checkbox } from './ui/checkbox';

interface ContentMigrationProps {
  isMigration: boolean;
  migrationType: string;
  postTypeCount: number;
  isContentUpload: boolean;
  contentUploadLevel: 'light' | 'medium' | 'heavy';
  onMigrationChange: (isMigration: boolean) => void;
  onMigrationTypeChange: (type: string) => void;
  onPostTypeCountChange: (count: number) => void;
  onContentUploadChange: (isContentUpload: boolean) => void;
  onContentUploadLevelChange: (level: 'light' | 'medium' | 'heavy') => void;
}

const ContentMigration: React.FC<ContentMigrationProps> = ({
  isMigration,
  migrationType,
  postTypeCount,
  isContentUpload,
  contentUploadLevel,
  onMigrationChange,
  onMigrationTypeChange,
  onPostTypeCountChange,
  onContentUploadChange,
  onContentUploadLevelChange
}) => {
  // Determine if we're dealing with post types or product types
  const isEcommerceMigration = migrationType === 'shopify-shopify' || migrationType === 'woocommerce-shopify';
  const typeLabel = isEcommerceMigration ? 'Product Types' : 'Post Types';
  
  // Calculate hours
  const migrationHours = isMigration ? postTypeCount * 2 : 0;
  
  // Calculate content upload hours based on level
  const getContentUploadHours = () => {
    if (!isContentUpload) return 0;
    switch (contentUploadLevel) {
      case 'light': return 4;
      case 'medium': return 8;
      case 'heavy': return 12;
      default: return 0;
    }
  };
  
  const contentUploadHours = getContentUploadHours();
  
  return (
    <div className="space-y-6">
      {/* Content Upload Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Content Upload</h3>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="content-upload-checkbox" 
            checked={isContentUpload}
            onCheckedChange={(checked) => onContentUploadChange(checked === true)}
          />
          <label
            htmlFor="content-upload-checkbox"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Developer to Upload Content
          </label>
        </div>
        
        {isContentUpload && (
          <div className="space-y-4 pl-6 border-l-2 border-primary/10">
            <div className="space-y-2">
              <label className="text-sm font-medium">Content Volume</label>
              
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  className={`px-4 py-2 text-sm rounded-md ${contentUploadLevel === 'light' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
                  onClick={() => onContentUploadLevelChange('light')}
                >
                  Light (4h)
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm rounded-md ${contentUploadLevel === 'medium' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
                  onClick={() => onContentUploadLevelChange('medium')}
                >
                  Medium (8h)
                </button>
                <button
                  type="button"
                  className={`px-4 py-2 text-sm rounded-md ${contentUploadLevel === 'heavy' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-secondary-foreground'}`}
                  onClick={() => onContentUploadLevelChange('heavy')}
                >
                  Heavy (12h)
                </button>
              </div>
              
              <div className="text-sm text-muted-foreground mt-2 inline-flex px-2 py-1 rounded-md bg-secondary">
                Adds {contentUploadHours} hours to estimate
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Content Migration Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Content Migration</h3>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="migration-checkbox" 
            checked={isMigration}
            onCheckedChange={(checked) => onMigrationChange(checked === true)}
          />
          <label
            htmlFor="migration-checkbox"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Migrate from existing site
          </label>
        </div>
        
        {isMigration && (
          <div className="space-y-4 pl-6 border-l-2 border-primary/10">
            <div className="space-y-2">
              <label htmlFor="migration-type" className="text-sm font-medium">
                Migration Type
              </label>
              <select
                id="migration-type"
                value={migrationType}
                onChange={(e) => onMigrationTypeChange(e.target.value)}
                className="w-full p-2 border rounded-md bg-background"
              >
                <option value="wordpress-wordpress">WordPress → WordPress</option>
                <option value="shopify-shopify">Shopify → Shopify</option>
                <option value="woocommerce-shopify">WooCommerce → Shopify</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>{typeLabel}</span>
                <span className="font-medium">{postTypeCount}</span>
              </div>
              
              <input
                type="range"
                min="1"
                max="15"
                value={postTypeCount}
                onChange={(e) => onPostTypeCountChange(parseInt(e.target.value))}
                className="w-full"
              />
              
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1</span>
                <span>15</span>
              </div>
              
              <div className="text-sm text-muted-foreground mt-2 inline-flex px-2 py-1 rounded-md bg-secondary">
                Adds {migrationHours} hours to estimate
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentMigration; 